/**
 * Media Library API Client
 *
 * Typed functions for interacting with the per-website media library.
 * Handles uploads (multipart), gallery listing, Pexels search, and stock image saving.
 */

import { apiGet, apiPost, apiRequest } from './client';

// =============================================================================
// TYPES
// =============================================================================

export interface MediaItem {
  id: string;
  website_id: string;
  source: 'pexels' | 'unsplash' | 'upload' | 'generated';
  source_id: string | null;
  r2_key: string;
  thumbnail_r2_key: string | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  title: string | null;
  original_filename: string | null;
  original_query: string | null;
  content_hash: string | null;
  is_active: number;
  used_in: string | null; // JSON array string
  usage_count: number;
  created_at: string;
  last_used_at: string | null;
  /** Public URL (computed by backend) */
  url: string;
  thumbnailUrl: string | null;
}

export interface PexelsPhoto {
  id: number;
  width: number;
  height: number;
  photographer: string;
  alt: string;
  avg_color: string;
  src: {
    original: string;
    large2x: string;
    large: string;
    medium: string;
    small: string;
    tiny: string;
  };
}

interface ListMediaResponse {
  media: MediaItem[];
  total: number;
}

interface UploadMediaResponse {
  media: MediaItem;
}

interface SearchPexelsResponse {
  photos: PexelsPhoto[];
  total_results: number;
  page: number;
  per_page: number;
}

interface SaveStockResponse {
  media: MediaItem;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * List all media for a website.
 * Supports filtering by source and active status.
 */
export function listMedia(
  websiteId: string,
  options?: { source?: string; activeOnly?: boolean; limit?: number; offset?: number }
): Promise<ListMediaResponse> {
  const params = new URLSearchParams();
  if (options?.source) params.set('source', options.source);
  if (options?.activeOnly) params.set('activeOnly', 'true');
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiGet<ListMediaResponse>(`/api/websites/${websiteId}/media${qs ? `?${qs}` : ''}`);
}

/**
 * Upload an image file to the media library.
 * Returns the new media record with R2 URL.
 */
export async function uploadMedia(
  websiteId: string,
  file: File,
  altText?: string,
  title?: string
): Promise<UploadMediaResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (altText) formData.append('altText', altText);
  if (title) formData.append('title', title);

  // Pass FormData directly — apiRequest detects FormData and omits the
  // application/json Content-Type so the browser sets multipart/form-data with
  // the correct boundary (required by the worker's request.formData()).
  return apiRequest<UploadMediaResponse>(`/api/websites/${websiteId}/media/upload`, {
    method: 'POST',
    body: formData,
  });
}

/**
 * Search Pexels for stock photos.
 * Does NOT save anything — just returns search results.
 */
export function searchPexels(
  websiteId: string,
  query: string,
  options?: { perPage?: number; page?: number; orientation?: string }
): Promise<SearchPexelsResponse> {
  return apiPost<SearchPexelsResponse>(`/api/websites/${websiteId}/media/search`, {
    query,
    perPage: options?.perPage ?? 20,
    page: options?.page ?? 1,
    orientation: options?.orientation,
  });
}

/**
 * Save a Pexels photo to the media library.
 * Downloads the image to R2 and creates a media record.
 */
export function saveStockImage(
  websiteId: string,
  photo: PexelsPhoto,
  originalQuery: string
): Promise<SaveStockResponse> {
  return apiPost<SaveStockResponse>(`/api/websites/${websiteId}/media/save-stock`, {
    photo,
    originalQuery,
  });
}

// =============================================================================
// Site Import (ADR-006)
// =============================================================================

export interface ImportCandidate {
  url: string;
  sourcePage: string;
  alt: string | null;
  origin: 'img' | 'srcset' | 'picture' | 'og' | 'background';
  /** Already in this website's media library from an earlier run. */
  alreadyImported: boolean;
}

export interface ScanSiteResult {
  success: boolean;
  message?: string;
  startUrl: string;
  pagesVisited: string[];
  pagesFailed: Array<{ url: string; error: string }>;
  candidates: ImportCandidate[];
  note: string;
}

export interface SaveOutcome {
  url: string;
  saved: boolean;
  mediaId?: string;
  width?: number;
  height?: number;
  reason?: string;
}

export interface SaveImportedResult {
  success: boolean;
  savedCount: number;
  outcomes: SaveOutcome[];
}

/** Read the customer's existing site. Writes nothing. */
export function scanSite(
  websiteId: string,
  url: string,
  maxPages?: number
): Promise<ScanSiteResult> {
  return apiPost<ScanSiteResult>(`/api/websites/${websiteId}/import/scan`, { url, maxPages });
}

/**
 * Save a batch of chosen images.
 *
 * The server caps a batch at 8 — 30 fetches plus 30 R2 writes in one request
 * would run into the gateway timeout. Callers send batches in sequence.
 */
export const IMPORT_BATCH_SIZE = 8;

export function saveImportedImages(
  websiteId: string,
  images: Array<Pick<ImportCandidate, 'url' | 'sourcePage' | 'alt' | 'origin'>>
): Promise<SaveImportedResult> {
  return apiPost<SaveImportedResult>(`/api/websites/${websiteId}/import/save`, { images });
}
